import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Response_Processing from "../../../../src/layout/Others/Response_Processing";
import Wrapper from "../../../components/CommonComponents/Wrapper";
import LeftWrapper from "../../../components/CommonComponents/LeftWrapper";
import FormContainer from "../../../components/CommonComponents/FormContainer";
import AdminSaveHeader from "../../../components/CommonComponents/AdminSaveHeader";
import MessageHeading from "../../../components/CommonComponents/MessageHeading";
import AdminNavbar from "../../../components/CommonComponents/AdminNavbar";
import { useMyContext } from "../../../components/Provider/CommonProvider";
import RightWrapper from "../../../components/CommonComponents/RightWrapper";
import AdminSwitch from "../../../components/CommonComponents/AdminSwitch";
import AdminImageUploadZone from "../../../components/CommonComponents/AdminImageUploadZone";
import MessageLabel from "../../../components/CommonComponents/MessageLabel";

function PlanContentSettings() {
    const { fetchRequest } = useMyContext();
    const access_token = localStorage.getItem("access_token");
    const headers = {
        "Content-Type": "application/json",
        Authorization: "Bearer " + access_token,
        Accept: "application/json",
        "Access-Control-Allow-Origin": "*",
    };

    const [showOverlay, setShowOverlay] = useState(false);
    const [content, setContent] = useState({});
    const [errors, setErrors] = useState({});

    // Fetch plan content on component mount
    const getPlanContent = async () => {
        setShowOverlay(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_Baseurl}/Front-End/site/plan-content-subscription`,
                { headers }
            );

            if (response?.data?.status) {
                setContent(response.data.data);
                toast.success('Plan content loaded successfully');
            } else {
                setDefaultContent();
                toast.info('Using default values');
            }
        } catch (error) {
            console.error('API Error:', error);
            setDefaultContent();

            if (error?.response?.status === 404) {
                toast.warning('API endpoint not found.');
            } else if (error?.response?.status === 500) {
                toast.error('Server error. Please run the database migration.');
            } else {
                toast.info('Using default values');
            }
        } finally {
            setShowOverlay(false);
        }
    };

    // Set default content values
    const setDefaultContent = () => {
        setContent({
            page_title: 'Membership and Prices',
            current_subscription_title: 'Current Subscription',
            active_badge_text: '✓ Active',
            label_plan_name: 'Plan Name',
            label_billing_cycle: 'Billing Cycle',
            label_price: 'Price',
            label_expires_on: 'Expires On',
            table_header_features: 'Features',
            feature_premium_content_title: 'Premium Content',
            feature_premium_content_desc: 'Movies, Web series, TV shows, download',
            status_feature_premium_content_title: 1,

            feature_music_title: 'Music',
            feature_music_desc: 'Unlimited Playlists',
            status_feature_music_title: 1,

            feature_live_tv_title: 'Live TV',
            status_feature_live_tv_title: 1,

            feature_ad_free_title: 'Plan Description',
            feature_ad_free_desc: 'For Premium Content',
            status_feature_ad_free_title: 1,

            feature_device_title: 'Device',
            status_feature_device_title: 1,

            feature_screens_title: 'No of screens',
            status_feature_screens_title: 1,

            feature_video_quality_title: 'Max Video Quality',
            status_feature_video_quality_title: 1,

            feature_audio_quality_title: 'Max Audio Quality',
            feature_audio_quality_desc: 'Dolby 5.1',
            status_feature_audio_quality_title: 1,

            payment_dropdown_label: 'Select Payment Method',
            buy_button_text: 'Buy Plan'
        });
    };

    useEffect(() => {
        getPlanContent();
    }, []);

    // Handle input changes
    const handleInput = (e) => {
        const { name, value } = e.target;
        setContent((prev) => ({
            ...prev,
            [name]: value,
        }));
        setErrors((prev) => ({
            ...prev,
            [name]: null,
        }));
    };

    const combinedRef = useRef({});

    // Handle Toggle Changes
    const handleInputenable = (e) => {
        const { name, checked } = e.target;
        setContent((prev) => ({
            ...prev,
            [name]: checked ? 1 : 0
        }));
    };

    // Handle File Changes
    const handleFileChange = (e) => {
        const { files } = e.target;
        if (files && files.length > 0) {
            const file = files[0];
            setContent((prev) => ({
                ...prev,
                plan_background_image_file: file
            }));
        }
    };

    const handleClearSelectFile = (name) => {
        setContent((prev) => ({
            ...prev,
            plan_background_image_file: null
        }));
        // Optional: If you want to allow clearing the 'saved' image from the UI state (not DB yet):
        // setContent((prev) => ({ ...prev, plan_background_image: "" }));
    };

    // Validate form
    const validateForm = () => {
        let formIsValid = true;
        const newErrors = {};

        const requiredFields = [
            'page_title',
            'current_subscription_title',
            'table_header_features',
            'payment_dropdown_label',
            'buy_button_text'
        ];

        requiredFields.forEach(field => {
            if (!content[field] || content[field].trim() === '') {
                newErrors[field] = 'This field cannot be empty';
                formIsValid = false;
            }
        });

        setErrors(newErrors);
        return formIsValid;
    };

    // Handle form submission
    const handleUpdate = async () => {
        if (!validateForm()) {
            toast.error("Please fill in all required fields");
            return;
        }

        setShowOverlay(true);
        try {
            const formData = new FormData();

            // Append file if selected
            if (content.plan_background_image_file) {
                formData.append('plan_background_image', content.plan_background_image_file);
            }

            // Prepare content object for JSON string (exclude file objects)
            const contentToSend = { ...content };
            delete contentToSend.plan_background_image_file;
            delete contentToSend.plan_background_image_preview;

            formData.append('content', JSON.stringify(contentToSend));

            const response = await axios.post(
                `${process.env.REACT_APP_Baseurl}/Front-End/site/plan-content-subscription`,
                formData,
                {
                    headers: {
                        ...headers,
                        "Content-Type": "multipart/form-data"
                    }
                }
            );

            if (response?.data?.status) {
                toast.success(response.data.message || "Plan content updated successfully");
                getPlanContent();
            } else {
                toast.error(response?.data?.message || "Failed to update plan content");
            }
        } catch (error) {
            console.error("Error updating plan content:", error);
            toast.error(error?.response?.data?.message || "Failed to update plan content");
        } finally {
            setShowOverlay(false);
        }
    };

    return (
        <>
            {showOverlay && <Response_Processing />}

            <div className="d-flex justify-content-between align-items-center mb-4">
                <MessageHeading text="Plan Page Content" className="admin-title pb-sm-0  text-md-start" noMargin />

                <button
                    className="btn btn-primary"
                    onClick={handleUpdate}
                    type="button"
                >
                    Update
                </button>
            </div>

            <FormContainer>

                <LeftWrapper>
                    <Wrapper>
                        {/* Page Headers Section */}
                        <MessageHeading text={"Page Details"} className="mb-2" noMargin />

                        <div className="text-start mt-2">
                            <p className=" theme-text-color mb-3 mt-0 admin-input-title d-block position-relative">Plan Title</p>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="page_title"
                                    value={content.page_title}
                                    onChange={handleInput}
                                    className="rs-input form-control-lg custom-input custom-input-border w-100 custom-placeholder bg-transparent theme-text-color mb-3"
                                    placeholder="Membership and Prices"
                                />
                            </div>
                            {errors.page_title && <span className="errorred"><p>{errors.page_title}</p></span>}
                        </div>

                        {/* Current Subscription Section */}

                        <div className="text-start">
                            <p className=" theme-text-color mb-3 mt-0 admin-input-title d-block position-relative">Current Subscription Title</p>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="current_subscription_title"
                                    value={content.current_subscription_title}
                                    onChange={handleInput}
                                    className="rs-input form-control-lg custom-input custom-input-border w-100 custom-placeholder bg-transparent theme-text-color mb-3"
                                    placeholder="Current Subscription"
                                />
                            </div>
                            {errors.current_subscription_title && <span className="errorred"><p>{errors.current_subscription_title}</p></span>}
                        </div>

                        <div className="text-start">
                            <p className="theme-text-color mb-3 mt-0 admin-input-title d-block position-relative">Active Badge</p>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="active_badge_text"
                                    value={content.active_badge_text}
                                    onChange={handleInput}
                                    className="rs-input form-control-lg custom-input custom-input-border w-100 custom-placeholder bg-transparent theme-text-color mb-3"
                                    placeholder="✓ Active"
                                />
                            </div>
                        </div>

                        <div className="text-start">
                            <p className="theme-text-color mb-3 mt-0 admin-input-title d-block position-relative">Plan Name Label</p>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="label_plan_name"
                                    value={content.label_plan_name}
                                    onChange={handleInput}
                                    className="rs-input form-control-lg custom-input custom-input-border w-100 custom-placeholder bg-transparent theme-text-color mb-3"
                                    placeholder="Plan Name"
                                />
                            </div>
                        </div>

                        <div className="text-start">
                            <p className="theme-text-color mb-3 mt-0 admin-input-title d-block position-relative">Billing Cycle Label</p>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="label_billing_cycle"
                                    value={content.label_billing_cycle}
                                    onChange={handleInput}
                                    className="rs-input form-control-lg custom-input custom-input-border w-100 custom-placeholder bg-transparent theme-text-color mb-3"
                                    placeholder="Billing Cycle"
                                />
                            </div>
                        </div>

                        <div className="text-start">
                            <p className="theme-text-color mb-3 mt-0 admin-input-title d-block position-relative">Price Label</p>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="label_price"
                                    value={content.label_price}
                                    onChange={handleInput}
                                    className="rs-input form-control-lg custom-input custom-input-border w-100 custom-placeholder bg-transparent theme-text-color mb-3"
                                    placeholder="Price"
                                />
                            </div>
                        </div>

                        <div className="text-start">
                            <p className="theme-text-color mb-3 mt-0 admin-input-title d-block position-relative">Expires On Label</p>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="label_expires_on"
                                    value={content.label_expires_on}
                                    onChange={handleInput}
                                    className="rs-input form-control-lg custom-input custom-input-border w-100 custom-placeholder bg-transparent theme-text-color mb-3"
                                    placeholder="Expires On"
                                />
                            </div>
                        </div>

                        {/* Comparison Table Section */}

                        <div className="text-start">
                            <p className=" theme-text-color mb-3 mt-0 admin-input-title d-block position-relative">Table Header</p>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="table_header_features"
                                    value={content.table_header_features}
                                    onChange={handleInput}
                                    className="rs-input form-control-lg custom-input custom-input-border w-100 custom-placeholder bg-transparent theme-text-color mb-3"
                                    placeholder="Features"
                                />
                            </div>
                            {errors.table_header_features && <span className="errorred"><p>{errors.table_header_features}</p></span>}
                        </div>

                        <div className="mt-4 mb-2">
                            <h6 className="fw-bold theme-text-color">Premium Content</h6>
                        </div>

                        <div className="text-start">
                            <p className="theme-text-color mb-3 mt-0 admin-input-title d-block position-relative">Title</p>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="feature_premium_content_title"
                                    value={content.feature_premium_content_title}
                                    onChange={handleInput}
                                    className="rs-input form-control-lg custom-input custom-input-border w-100 custom-placeholder bg-transparent theme-text-color mb-3"
                                    placeholder="Premium Content"
                                />
                            </div>
                        </div>

                        <div className="text-start">
                            <p className="theme-text-color mb-3 mt-0 admin-input-title d-block position-relative">Description</p>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="feature_premium_content_desc"
                                    value={content.feature_premium_content_desc}
                                    onChange={handleInput}
                                    className="rs-input form-control-lg custom-input custom-input-border w-100 custom-placeholder bg-transparent theme-text-color mb-3"
                                    placeholder="Movies, Web series, TV shows, download"
                                />
                            </div>
                        </div>

                        <div className="mt-4 mb-2">
                            <h6 className="fw-bold theme-text-color">Music</h6>
                        </div>

                        <div className="text-start">
                            <p className="theme-text-color mb-3 mt-0 admin-input-title d-block position-relative">Title</p>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="feature_music_title"
                                    value={content.feature_music_title}
                                    onChange={handleInput}
                                    className="rs-input form-control-lg custom-input custom-input-border w-100 custom-placeholder bg-transparent theme-text-color mb-3"
                                    placeholder="Music"
                                />
                            </div>
                        </div>

                        <div className="text-start">
                            <p className="theme-text-color mb-3 mt-0 admin-input-title d-block position-relative">Description</p>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="feature_music_desc"
                                    value={content.feature_music_desc}
                                    onChange={handleInput}
                                    className="rs-input form-control-lg custom-input custom-input-border w-100 custom-placeholder bg-transparent theme-text-color mb-3"
                                    placeholder="Unlimited Playlists"
                                />
                            </div>
                        </div>

                        <div className="mt-4 mb-2">
                            <h6 className="fw-bold theme-text-color">Other Features</h6>
                        </div>

                        <div className="text-start">
                            <p className="theme-text-color mb-3 mt-0 admin-input-title d-block position-relative">Live TV Title</p>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="feature_live_tv_title"
                                    value={content.feature_live_tv_title}
                                    onChange={handleInput}
                                    className="rs-input form-control-lg custom-input custom-input-border w-100 custom-placeholder bg-transparent theme-text-color mb-3"
                                    placeholder="Live TV"
                                />
                            </div>
                        </div>

                        <div className="text-start">
                            <p className="theme-text-color mb-3 mt-0 admin-input-title d-block position-relative">Plan Description Title</p>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="feature_ad_free_title"
                                    value={content.feature_ad_free_title}
                                    onChange={handleInput}
                                    className="rs-input form-control-lg custom-input custom-input-border w-100 custom-placeholder bg-transparent theme-text-color mb-3"
                                    placeholder="Plan Description"
                                />
                            </div>
                        </div>

                        {/* <div className="text-start">
                            <p className="theme-text-color mb-3 mt-0 admin-input-title d-block position-relative">Description Description</p>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="feature_ad_free_desc"
                                    value={content.feature_ad_free_desc}
                                    onChange={handleInput}
                                    className="rs-input form-control-lg custom-input custom-input-border w-100 custom-placeholder bg-transparent theme-text-color mb-3"
                                    placeholder="For Premium Content"
                                />
                            </div>
                        </div> */}

                        <div className="text-start">
                            <p className="theme-text-color mb-3 mt-0 admin-input-title d-block position-relative">Device Title</p>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="feature_device_title"
                                    value={content.feature_device_title}
                                    onChange={handleInput}
                                    className="rs-input form-control-lg custom-input custom-input-border w-100 custom-placeholder bg-transparent theme-text-color mb-3"
                                    placeholder="Device"
                                />
                            </div>
                        </div>

                        <div className="text-start">
                            <p className="theme-text-color mb-3 mt-0 admin-input-title d-block position-relative">Screens Title</p>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="feature_screens_title"
                                    value={content.feature_screens_title}
                                    onChange={handleInput}
                                    className="rs-input form-control-lg custom-input custom-input-border w-100 custom-placeholder bg-transparent theme-text-color mb-3"
                                    placeholder="No of screens"
                                />
                            </div>
                        </div>

                        <div className="text-start">
                            <p className="theme-text-color mb-3 mt-0 admin-input-title d-block position-relative">Video Quality Title</p>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="feature_video_quality_title"
                                    value={content.feature_video_quality_title}
                                    onChange={handleInput}
                                    className="rs-input form-control-lg custom-input custom-input-border w-100 custom-placeholder bg-transparent theme-text-color mb-3"
                                    placeholder="Max Video Quality"
                                />
                            </div>
                        </div>

                        <div className="text-start">
                            <p className="theme-text-color mb-3 mt-0 admin-input-title d-block position-relative">Audio Quality Title</p>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="feature_audio_quality_title"
                                    value={content.feature_audio_quality_title}
                                    onChange={handleInput}
                                    className="rs-input form-control-lg custom-input custom-input-border w-100 custom-placeholder bg-transparent theme-text-color mb-3"
                                    placeholder="Max Audio Quality"
                                />
                            </div>
                        </div>

                        <div className="text-start">
                            <p className="theme-text-color mb-3 mt-0 admin-input-title d-block position-relative">Audio Quality Description</p>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="feature_audio_quality_desc"
                                    value={content.feature_audio_quality_desc}
                                    onChange={handleInput}
                                    className="rs-input form-control-lg custom-input custom-input-border w-100 custom-placeholder bg-transparent theme-text-color mb-3"
                                    placeholder="Dolby 5.1"
                                />
                            </div>
                        </div>

                        {/* Payment Section */}

                        <div className="text-start">
                            <p className=" theme-text-color mb-3 mt-0 admin-input-title d-block position-relative">Payment Dropdown Label</p>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="payment_dropdown_label"
                                    value={content.payment_dropdown_label}
                                    onChange={handleInput}
                                    className="rs-input form-control-lg custom-input custom-input-border w-100 custom-placeholder bg-transparent theme-text-color mb-3"
                                    placeholder="Select Payment Method"
                                />
                            </div>
                            {errors.payment_dropdown_label && <span className="errorred"><p>{errors.payment_dropdown_label}</p></span>}
                        </div>

                        <div className="text-start">
                            <p className=" theme-text-color mb-3 mt-0 admin-input-title d-block position-relative">Buy Button Text</p>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="buy_button_text"
                                    value={content.buy_button_text}
                                    onChange={handleInput}
                                    className="rs-input form-control-lg custom-input custom-input-border w-100 custom-placeholder bg-transparent theme-text-color mb-3"
                                    placeholder="Buy Plan"
                                />
                            </div>
                            {errors.buy_button_text && <span className="errorred"><p>{errors.buy_button_text}</p></span>}
                        </div>
                    </Wrapper>
                </LeftWrapper>

                <RightWrapper>

                    <Wrapper>
                        <MessageHeading text="Thumbnails" />
                        <MessageLabel text="Image" />
                        {/* <p className='mb-3 mt-0 theme-text-color opacity-75 admin-input-description'>Select the image (1920x400px or 5:1 Ratio)</p> */}

                        <div className="mt-2">
                            <AdminImageUploadZone
                                onChange={handleFileChange}
                                selectedFile={content.plan_background_image_file}
                                uploadedImage={content.plan_background_image}
                                onDelete={handleClearSelectFile}
                                ref={(el) => (combinedRef.current.plan_background_image = el)}
                                name="plan_background_image"
                                id="plan_background_image_upload"
                                accept="image/jpeg,image/png,image/jpg"
                                error={errors.plan_background_image}
                            />
                        </div>

                        <div className="text-start mt-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <p className=" theme-text-color mb-0 mt-0 admin-input-title d-block position-relative">Background Image enable</p>
                                <AdminSwitch
                                    name="status_plan_background_image"
                                    checked={Number(content.status_plan_background_image) === 1}
                                    onChange={handleInputenable}
                                />
                            </div>

                        </div>
                    </Wrapper>

                    <Wrapper>
                        <MessageHeading text={"Status Settings"} className="mb-2" noMargin />

                        <div className="d-flex justify-content-between mb-3 align-items-center">
                            <div className="col-10">
                                <p className="theme-text-color">Premium Content</p>
                            </div>
                            <div className="col-2 text-end">
                                <AdminSwitch name="status_feature_premium_content_title" checked={Number(content.status_feature_premium_content_title) === 1} onChange={handleInputenable} />
                            </div>
                        </div>

                        <div className="d-flex justify-content-between mb-3 align-items-center">
                            <div className="col-10">
                                <p className="theme-text-color">Music</p>
                            </div>
                            <div className="col-2 text-end">
                                <AdminSwitch name="status_feature_music_title" checked={Number(content.status_feature_music_title) === 1} onChange={handleInputenable} />
                            </div>
                        </div>

                        <div className="d-flex justify-content-between mb-3 align-items-center">
                            <div className="col-10">
                                <p className="theme-text-color">Live TV</p>
                            </div>
                            <div className="col-2 text-end">
                                <AdminSwitch name="status_feature_live_tv_title" checked={Number(content.status_feature_live_tv_title) === 1} onChange={handleInputenable} />
                            </div>
                        </div>

                        <div className="d-flex justify-content-between mb-3 align-items-center">
                            <div className="col-10">
                                <p className="theme-text-color">Plan Description</p>
                            </div>
                            <div className="col-2 text-end">
                                <AdminSwitch name="status_feature_ad_free_title" checked={Number(content.status_feature_ad_free_title) === 1} onChange={handleInputenable} />
                            </div>
                        </div>

                        <div className="d-flex justify-content-between mb-3 align-items-center">
                            <div className="col-10">
                                <p className="theme-text-color">Device</p>
                            </div>
                            <div className="col-2 text-end">
                                <AdminSwitch name="status_feature_device_title" checked={Number(content.status_feature_device_title) === 1} onChange={handleInputenable} />
                            </div>
                        </div>

                        <div className="d-flex justify-content-between mb-3 align-items-center">
                            <div className="col-10">
                                <p className="theme-text-color">Screens</p>
                            </div>
                            <div className="col-2 text-end">
                                <AdminSwitch name="status_feature_screens_title" checked={Number(content.status_feature_screens_title) === 1} onChange={handleInputenable} />
                            </div>
                        </div>

                        <div className="d-flex justify-content-between mb-3 align-items-center">
                            <div className="col-10">
                                <p className="theme-text-color">Video Quality</p>
                            </div>
                            <div className="col-2 text-end">
                                <AdminSwitch name="status_feature_video_quality_title" checked={Number(content.status_feature_video_quality_title) === 1} onChange={handleInputenable} />
                            </div>
                        </div>

                        <div className="d-flex justify-content-between mb-3 align-items-center">
                            <div className="col-10">
                                <p className="theme-text-color">Audio Quality</p>
                            </div>
                            <div className="col-2 text-end">
                                <AdminSwitch name="status_feature_audio_quality_title" checked={Number(content.status_feature_audio_quality_title) === 1} onChange={handleInputenable} />
                            </div>
                        </div>
                    </Wrapper>
                </RightWrapper>

                {/* <div className="d-flex justify-content-end mt-4">
                        <button
                            className="btn btn-primary"
                            onClick={handleUpdate}
                            type="button"
                        >
                            Update                                 </button>
                    </div> */}
            </FormContainer>

        </>
    );
}

export default PlanContentSettings;
